'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// useLayoutEffect runs before paint on the client and is a no-op on the server.
// Pre-selecting at module scope avoids the React warning for using
// useLayoutEffect during SSR.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
import Image from 'next/image';
import { Play, Timer } from 'lucide-react';
import { useFormatter, useLocale } from 'next-intl';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';
import { type Locale } from '@/i18n/config';
import { CheckeredFlagIcon } from './components/CheckeredFlagIcon';
import { CountdownTile } from './components/CountdownTile';
import { EmptyState } from './components/EmptyState';
import { HorizontalTimeline } from './components/HorizontalTimeline';
import { RideDayCTACards } from './components/RideDayCTACards';
import { VerticalTimeline } from './components/VerticalTimeline';
import {
  DGD_DRIVE_DURATION_MS,
  FIRST_DRIVE_UTC_MS,
  DRIVE_DAY_NOON_UTC_MS,
  DRIVE_DAY_WINDOW_MS,
} from './lib/constants';
import { resolveEventOffsetHours } from './lib/country-timezones';
import { computeRideStartUtcMs, formatHHMM, formatLocalHHMM } from './lib/ride-day-math';
import type {
  CountryTimelineStatus,
  GlobalRideRow,
  RideTimelineEntry,
  RideTimelineStatus,
} from './types';

interface LivePageProps {
  prefetchedData?: { globalMapRides: GlobalRideRow[]; currencyCode: string };
}

export function LivePage({ prefetchedData }: LivePageProps = {}) {
  const format = useFormatter();
  const locale = useLocale() as Locale;
  const { currency: regionalCurrency } = useRegional();
  const staticNumberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);

  // Real-time clock for both countdown + live status. Init to a stable 0 so
  // SSR and the first client render produce identical HTML (no Date.now()
  // mismatch). The useEffect below populates real time once on the client;
  // we gate the countdown JSX on nowMs > 0 so it only renders post-mount.
  const [nowMs, setNowMs] = useState(0);
  useEffect(() => {
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const [rideDayView, setRideDayView] = useState<'horizontal' | 'vertical'>('horizontal');
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);
  const [horizontalZoom, setHorizontalZoom] = useState(1);

  // Simulation: opt-in via ?simulation (or ?simulate / ?demo) query param.
  // When enabled, the page replays 24h of real drive-day data compressed
  // into a 60-second loop so the design can be QA'd on production data
  // ahead of drive day.
  const [simulationEnabled, setSimulationEnabled] = useState(false);
  const [simulate, setSimulate] = useState(false);
  const simStartRef = useRef<number>(0);
  const [simElapsedMs, setSimElapsedMs] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('simulation') || params.has('simulate') || params.has('demo')) {
      setSimulationEnabled(true);
      setSimulate(true);
    }
  }, []);

  useEffect(() => {
    if (!simulate) return;
    simStartRef.current = Date.now();
    setSimElapsedMs(0);
    const id = setInterval(() => {
      setSimElapsedMs((Date.now() - simStartRef.current) % 60_000);
    }, 100);
    return () => clearInterval(id);
  }, [simulate]);

  // Default to vertical view on mobile (only on initial mount). Layout-effect
  // variant fires before the browser paints, so mobile users don't see a
  // horizontal flash on first render.
  const didInitRideDayView = useRef(false);
  useIsomorphicLayoutEffect(() => {
    if (didInitRideDayView.current) return;
    didInitRideDayView.current = true;
    if (window.innerWidth < 768) setRideDayView('vertical');
  }, []);

  // Production drive data. Hard-cached on both ends — the page computes
  // live/upcoming/completed status client-side from each ride's start time,
  // so the displayed status ticks every second from the local clock and
  // doesn't depend on a fresh fetch. We trade freshness of raised totals /
  // registration counts for resilience against load spikes on drive day.
  //  - Server (Redis via cachedPublicQuery): 1 hour
  //    (override via PUBLIC_QUERY_CACHE_LIVE_TTL_MS)
  //  - Client (React Query): 1 hour staleTime, 2 hour gcTime, refetched
  //    every 15 min so the page stays warm without hammering the API.
  const { data: globalRidesRaw = [] } = trpc.publicLookups.globalMapRidesLive.useQuery(
    { currencyCode: regionalCurrency },
    {
      staleTime: 60 * 60 * 1000,
      gcTime: 2 * 60 * 60 * 1000,
      refetchInterval: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Only seed React Query when the server-rendered payload matches the
      // user's current currency. If the currency changes client-side (regional
      // picker), the initialData would be stale, so let the query fire fresh.
      ...(prefetchedData &&
        prefetchedData.currencyCode === regionalCurrency && {
          initialData: prefetchedData.globalMapRides as never,
        }),
    },
  );

  const globalRides = useMemo<GlobalRideRow[]>(
    () => globalRidesRaw as GlobalRideRow[],
    [globalRidesRaw],
  );

  // Base aggregation (by country) — driven entirely by real ride data.
  const rideDayTimelineBase = useMemo(() => {
    const sourceRides = globalRides;
    const buckets = new Map<
      string,
      {
        countryCode: string;
        countryName: string;
        rides: {
          id: string;
          city: string;
          startMs: number;
          endMs: number;
          participants: number;
          participantsLabel: string;
          utcLabel: string;
          localLabel: string;
        }[];
      }
    >();

    for (const ride of sourceRides) {
      if (!ride.eventDate) continue;
      // Filter to events near drive day (±24h of UTC noon Sept 27 2026)
      // so timezone-offset variations don't drop legitimate matches.
      // startTime is not required — events without one are placed at 9am
      // local so every drive day event lands on the timeline.
      const eventMs = new Date(ride.eventDate).getTime();
      if (!Number.isFinite(eventMs)) continue;
      if (Math.abs(eventMs - DRIVE_DAY_NOON_UTC_MS) > DRIVE_DAY_WINDOW_MS) continue;
      if (ride.postponedDate) continue;
      // Resolve the event's civil timezone from its country code (DST-aware
      // for the ride date). Coordinates aren't used — raw longitude gives
      // solar time, which is off by up to an hour from real wall-clock time.
      const offsetHours = resolveEventOffsetHours(ride.countryCode, eventMs);
      const startMs = computeRideStartUtcMs(ride.eventDate, ride.startTime, offsetHours);
      if (startMs === null) continue;
      const rideMeta = {
        id: ride.id,
        city: ride.city,
        startMs,
        endMs: startMs + DGD_DRIVE_DURATION_MS,
        participants: ride.currentRegistrations,
        participantsLabel: staticNumberFormatter.format(ride.currentRegistrations),
        utcLabel: formatHHMM(startMs),
        localLabel: formatLocalHHMM(startMs, ride.countryCode),
      };
      const existing = buckets.get(ride.countryCode);
      if (existing) existing.rides.push(rideMeta);
      else
        buckets.set(ride.countryCode, {
          countryCode: ride.countryCode,
          countryName: ride.countryName,
          rides: [rideMeta],
        });
    }

    const entries = Array.from(buckets.values()).map((bucket) => {
      bucket.rides.sort((a, b) => a.startMs - b.startMs);
      const participantsTotal = bucket.rides.reduce((acc, ride) => acc + ride.participants, 0);
      return {
        countryCode: bucket.countryCode,
        countryName: bucket.countryName,
        rideCount: bucket.rides.length,
        rideCountLabel: staticNumberFormatter.format(bucket.rides.length),
        earliestStartUtcMs: bucket.rides[0].startMs,
        latestStartUtcMs: bucket.rides[bucket.rides.length - 1].startMs,
        participantsTotal,
        participantsTotalLabel: staticNumberFormatter.format(participantsTotal),
        rides: bucket.rides.map((ride) => ({
          id: ride.id,
          city: ride.city,
          startUtcMs: ride.startMs,
          endUtcMs: ride.endMs,
          participants: ride.participants,
          participantsLabel: ride.participantsLabel,
          utcLabel: ride.utcLabel,
          localLabel: ride.localLabel,
        })),
      };
    });

    entries.sort((a, b) => a.earliestStartUtcMs - b.earliestStartUtcMs);
    const totalRides = entries.reduce((acc, e) => acc + e.rideCount, 0);
    const firstStart = entries.length ? entries[0].earliestStartUtcMs : null;
    const lastStart = entries.length ? Math.max(...entries.map((e) => e.latestStartUtcMs)) : null;
    // End the timeline at the last ride's finish — don't pad out to a full
    // 24h. When the latest start + ride duration is well before 24h after
    // the first start, that extra padding leaves a large empty stretch on
    // the right of the horizontal view (and an unused "Day Ends" gap).
    const timelineEnd =
      firstStart !== null && lastStart !== null ? lastStart + DGD_DRIVE_DURATION_MS : null;

    return {
      entries,
      totalRides,
      timelineStart: firstStart,
      timelineEnd,
      timelineMarkerStart: firstStart,
      timelineMarkerEnd: lastStart,
    };
  }, [globalRides, staticNumberFormatter]);

  // When simulation is active, map the 60s loop progress to a virtual
  // "now" inside the real ride-day timeline window so every ride
  // transitions upcoming → live → completed on real data.
  const simulatedNowMs = useMemo(() => {
    if (!simulate) return null;
    if (rideDayTimelineBase.timelineStart === null || rideDayTimelineBase.timelineEnd === null) {
      return null;
    }
    const span = rideDayTimelineBase.timelineEnd - rideDayTimelineBase.timelineStart;
    const progress = (simElapsedMs % 60_000) / 60_000;
    return rideDayTimelineBase.timelineStart + progress * span;
  }, [simulate, simElapsedMs, rideDayTimelineBase]);

  const effectiveNowMs = simulatedNowMs ?? nowMs;

  // Status-resolved timeline
  const rideDayTimeline = useMemo(() => {
    let totalCompleted = 0;
    let totalLive = 0;
    let totalUpcoming = 0;
    let totalParticipants = 0;
    let totalParticipantsCompleted = 0;
    let totalParticipantsLive = 0;
    let totalParticipantsUpcoming = 0;
    const statusByCountryCode: Record<string, CountryTimelineStatus> = {};

    for (const e of rideDayTimelineBase.entries) {
      let completedCount = 0;
      let liveCount = 0;
      let upcomingCount = 0;
      let participantsCompleted = 0;
      let participantsLive = 0;
      let participantsUpcoming = 0;

      for (const r of e.rides) {
        if (effectiveNowMs >= r.endUtcMs) {
          completedCount += 1;
          participantsCompleted += r.participants;
        } else if (effectiveNowMs >= r.startUtcMs) {
          liveCount += 1;
          participantsLive += r.participants;
        } else {
          upcomingCount += 1;
          participantsUpcoming += r.participants;
        }
      }

      totalCompleted += completedCount;
      totalLive += liveCount;
      totalUpcoming += upcomingCount;
      totalParticipants += e.participantsTotal;
      totalParticipantsCompleted += participantsCompleted;
      totalParticipantsLive += participantsLive;
      totalParticipantsUpcoming += participantsUpcoming;
      let status: RideTimelineStatus = 'upcoming';
      if (liveCount > 0) status = 'live';
      else if (completedCount === e.rideCount) status = 'completed';

      statusByCountryCode[e.countryCode] = {
        completedCount,
        liveCount,
        upcomingCount,
        participantsCompleted,
        participantsLive,
        participantsUpcoming,
        status,
      };
    }

    return {
      entries: rideDayTimelineBase.entries,
      statusByCountryCode,
      totalRides: rideDayTimelineBase.totalRides,
      totalCompleted,
      totalLive,
      totalUpcoming,
      totalParticipants,
      totalParticipantsCompleted,
      totalParticipantsLive,
      totalParticipantsUpcoming,
      timelineStart: rideDayTimelineBase.timelineStart,
      timelineEnd: rideDayTimelineBase.timelineEnd,
      timelineMarkerStart: rideDayTimelineBase.timelineMarkerStart,
      timelineMarkerEnd: rideDayTimelineBase.timelineMarkerEnd,
    };
  }, [rideDayTimelineBase, effectiveNowMs]);

  const nowPositionPct = useMemo(() => {
    if (
      rideDayTimeline.timelineMarkerStart === null ||
      rideDayTimeline.timelineMarkerEnd === null
    ) {
      return null;
    }
    const span = rideDayTimeline.timelineMarkerEnd - rideDayTimeline.timelineMarkerStart;
    if (span <= 0) return null;
    const pct = ((effectiveNowMs - rideDayTimeline.timelineMarkerStart) / span) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [effectiveNowMs, rideDayTimeline]);

  // Countdown until first drive
  const countdownToFirstRide = useMemo(() => {
    const diff = Math.max(0, FIRST_DRIVE_UTC_MS - nowMs);
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      total: diff,
    };
  }, [nowMs]);

  const eventHasStarted = countdownToFirstRide.total === 0;

  return (
    <div className="bg-dgd-gold-tint">
      {/* ────── Dark Header Band ────── */}
      <div className="relative overflow-hidden border-b border-dgd-gold/20 bg-stone-950">
        {/* Subtle gold radial accents */}
        <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-dgd-gold/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-dgd-gold/10 blur-3xl" />
        <div className="container relative py-20 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-dgd-display text-5xl uppercase leading-[0.95] tracking-tight text-white md:text-7xl lg:text-[7.5rem]">
              Drive Day Tracker <span className="text-dgd-gold">2026</span>
            </h1>
            <p className="mt-4 text-base text-stone-400 md:text-lg">
              {eventHasStarted
                ? 'Drives are rolling out around the world right now.'
                : 'The first drives start in New Zealand. Counting down to the kickoff.'}
            </p>
          </div>

          {/* Countdown (until first drive starts). nowMs > 0 gate keeps SSR
              and first client render identical; values populate after mount. */}
          {nowMs > 0 && !eventHasStarted && (
            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-dgd-gold">
                First drives · Nelson &amp; Tauranga 9:00am NZDT
              </p>
              <div className="flex gap-3 sm:gap-5">
                <CountdownTile value={countdownToFirstRide.days} label="Days" />
                <CountdownTile value={countdownToFirstRide.hours} label="Hours" />
                <CountdownTile value={countdownToFirstRide.minutes} label="Minutes" />
                <CountdownTile value={countdownToFirstRide.seconds} label="Seconds" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ────── Cream Live Tracker section ────── */}
      <div className="relative bg-dgd-gold-tint">
        <div className="container relative py-16 md:py-24">
          {/* Live tracker section */}
          <div>
            <div className="flex flex-wrap items-center justify-end gap-3">
              {simulate && (
                <span className="inline-flex items-center gap-1 rounded-full border border-dgd-gold-hover/40 bg-dgd-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dgd-gold-hover">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-dgd-gold-hover" />
                  Demo · 24h in 1m
                </span>
              )}
              {simulationEnabled && (
                <button
                  type="button"
                  onClick={() => setSimulate((s) => !s)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                    simulate
                      ? 'border-dgd-gold-hover bg-dgd-gold-hover text-white hover:bg-stone-950'
                      : 'border-stone-950/20 bg-white/70 text-stone-800 hover:border-dgd-gold-hover/60 hover:text-stone-950'
                  }`}
                >
                  <Play className="h-3 w-3 fill-current" />
                  {simulate ? 'Stop simulation' : 'Simulate drive day (1 min loop)'}
                </button>
              )}
              <div className="inline-flex rounded-full border border-stone-950/15 bg-white/70 p-0.5 text-xs font-medium">
                {(
                  [
                    { id: 'horizontal', label: 'Horizontal' },
                    { id: 'vertical', label: 'Vertical' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setRideDayView(opt.id);
                      setExpandedCountry(null);
                    }}
                    className={`rounded-full px-3.5 py-1 transition-colors ${
                      rideDayView === opt.id
                        ? 'bg-stone-950 text-white'
                        : 'text-stone-700 hover:text-stone-950'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status ribbon */}
            <div className="mt-6 overflow-hidden rounded-xl border border-dgd-gold/30 bg-dgd-gold/[0.07]">
              <div className="grid grid-cols-2 sm:grid-cols-4">
                {(
                  [
                    {
                      label: 'Driving now',
                      rides: rideDayTimeline.totalLive,
                      participants: rideDayTimeline.totalParticipantsLive,
                      kind: 'live' as const,
                    },
                    {
                      label: 'Completed',
                      rides: rideDayTimeline.totalCompleted,
                      participants: rideDayTimeline.totalParticipantsCompleted,
                      kind: 'completed' as const,
                    },
                    {
                      label: 'Starting soon',
                      rides: rideDayTimeline.totalUpcoming,
                      participants: rideDayTimeline.totalParticipantsUpcoming,
                      kind: 'upcoming' as const,
                    },
                    {
                      label: 'Today total',
                      rides: rideDayTimeline.totalRides,
                      participants: rideDayTimeline.totalParticipants,
                      kind: 'total' as const,
                    },
                  ] as const
                ).map((s, i) => {
                  // Border placement: visual grid is 2×2 on mobile, 1×4 on desktop.
                  // Right borders are between columns; top borders are between
                  // the two mobile rows only.
                  const borderClasses = [
                    i === 0 && 'border-r border-dgd-gold/20',
                    i === 1 && 'sm:border-r sm:border-dgd-gold/20',
                    i === 2 && 'border-r border-t border-dgd-gold/20 sm:border-t-0',
                    i === 3 && 'border-t border-dgd-gold/20 sm:border-t-0 sm:border-r-0',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const formatStat = (n: number) =>
                    n >= 10_000
                      ? format.number(n, { notation: 'compact', maximumFractionDigits: 1 })
                      : format.number(n);
                  const ridesDisplay = formatStat(s.rides);
                  const participantsDisplay = formatStat(s.participants);
                  return (
                    <div
                      key={s.label}
                      className={`flex min-w-0 flex-col gap-3 px-3 py-4 sm:gap-3.5 sm:px-4 ${borderClasses}`}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="flex h-4 flex-shrink-0 items-center sm:h-5">
                          {s.kind === 'live' ? (
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-dgd-gold opacity-75" />
                              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-dgd-gold" />
                            </span>
                          ) : s.kind === 'completed' ? (
                            <CheckeredFlagIcon className="h-3.5 w-3.5 text-stone-700" />
                          ) : s.kind === 'upcoming' ? (
                            <Timer className="h-3.5 w-3.5 text-stone-700" strokeWidth={2} />
                          ) : (
                            <span className="h-2 w-2 rounded-full bg-stone-900" />
                          )}
                        </div>
                        <span className="truncate text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-600 sm:text-[11px]">
                          {s.label}
                        </span>
                      </div>
                      <div className="grid min-w-0 grid-cols-2 gap-x-2">
                        <div className="flex min-w-0 flex-col">
                          <span
                            title={format.number(s.rides)}
                            className="text-base font-bold leading-none tabular-nums text-stone-950 sm:text-lg md:text-xl"
                          >
                            {ridesDisplay}
                          </span>
                          <span className="mt-1 text-[9px] uppercase tracking-wider text-stone-500">
                            {s.rides === 1 ? 'drive' : 'drives'}
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-col">
                          <span
                            title={format.number(s.participants)}
                            className="text-base font-bold leading-none tabular-nums text-stone-950 sm:text-lg md:text-xl"
                          >
                            {participantsDisplay}
                          </span>
                          <span className="mt-1 text-[9px] uppercase tracking-wider text-stone-500">
                            {s.participants === 1 ? 'driver' : 'drivers'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Timeline track */}
            <div className="mt-12">
              {rideDayTimeline.entries.length === 0 ? (
                <EmptyState
                  daysUntilFirstRide={countdownToFirstRide.days}
                  eventHasStarted={eventHasStarted}
                  nowMs={nowMs}
                />
              ) : rideDayView === 'horizontal' ? (
                <HorizontalTimeline
                  rideDayTimeline={rideDayTimeline}
                  expandedCountry={expandedCountry}
                  setExpandedCountry={setExpandedCountry}
                  horizontalZoom={horizontalZoom}
                  setHorizontalZoom={setHorizontalZoom}
                  nowPositionPct={nowPositionPct}
                  effectiveNowMs={effectiveNowMs}
                />
              ) : (
                <VerticalTimeline
                  rideDayTimeline={rideDayTimeline}
                  expandedCountry={expandedCountry}
                  setExpandedCountry={setExpandedCountry}
                  nowPositionPct={nowPositionPct}
                  effectiveNowMs={effectiveNowMs}
                />
              )}
            </div>
          </div>

          <div className="mt-20 flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-stone-700 md:text-base">
              Supported by{' '}
              <a
                href="https://jamcontent.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-stone-950 underline-offset-4 transition-colors hover:text-dgd-gold-hover hover:underline"
              >
                Jam Content
              </a>
            </p>
            <a
              href="https://jamcontent.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <Image
                src="/images/dgd/LogoJam_Dark.webp"
                alt="Jam Content"
                width={160}
                height={48}
                className="h-9 w-auto md:h-10"
              />
            </a>
          </div>

          <RideDayCTACards locale={locale} />
        </div>
      </div>
    </div>
  );
}
